



import { useEffect, useState } from "react";
import { AdminMonthlyAttendanceApi } from "../../app/api";
import { useAuth } from "../../app/store";

export default function UserAttendance({ userId }) {
  const { user } = useAuth();

  const today = new Date();

  // ✅ NEW: FROM & TO
  const [fromMonth, setFromMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
  );

  const [toMonth, setToMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
  );

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ UPDATED useEffect
  useEffect(() => {
    fetchSummary();
  }, [fromMonth, toMonth, userId, user]);

  // ✅ MONTH RANGE HELPER
  const getMonthRange = (start, end) => {
    const months = [];
    let current = new Date(start + "-01");
    const last = new Date(end + "-01");

    while (current <= last) {
      months.push({
        year: current.getFullYear(),
        month: String(current.getMonth() + 1).padStart(2, "0"),
      });
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);

      const months = getMonthRange(fromMonth, toMonth);

      let finalData = null;

      for (let m of months) {
        const res = await AdminMonthlyAttendanceApi(
          m.month,
          m.year,
          userId
        );

        if (res?.success && res?.data?.length > 0) {
          let userData = null;

          // ✅ SAME ROLE LOGIC (NO CHANGE)
          if (user?.role_id === 1) {
            userData = userId
              ? res.data.find((u) => String(u.user_id) === String(userId))
              : res.data[0];
          } else {
            userData = res.data.find(
              (u) => String(u.user_id) === String(user?.id)
            );
          }

          if (!userData) continue;

          // ✅ MERGE DATA
          if (!finalData) {
            finalData = { ...userData };
          } else {
            finalData.present_days += userData.present_days || 0;
            finalData.absent_days += userData.absent_days || 0;
            finalData.late_days += userData.late_days || 0;
            finalData.early_out_days += userData.early_out_days || 0;

            const addTime = (t1, t2) => {
              const toSec = (t) => {
                const [h, m, s] = t.split(":").map(Number);
                return h * 3600 + m * 60 + s;
              };

              const total = toSec(t1) + toSec(t2);

              const h = Math.floor(total / 3600);
              const m = Math.floor((total % 3600) / 60);
              const s = total % 60;

              return `${String(h).padStart(2, "0")}:${String(m).padStart(
                2,
                "0"
              )}:${String(s).padStart(2, "0")}`;
            };

            finalData.total_working_hours = addTime(
              finalData.total_working_hours,
              userData.total_working_hours
            );

            finalData.extra_hours = addTime(
              finalData.extra_hours,
              userData.extra_hours
            );
          }
        }
      }

      setSummary(finalData);
    } catch (err) {
      console.error("Attendance summary error:", err);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const totalWorkingDays =
    (summary?.present_days || 0) +
    (summary?.absent_days || 0);


  return (
    <div className="text-white">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row gap-2 justify-between items-center mb-6">
        <h3 className="text-xl font-semibold whitespace-nowrap">
          Monthly Attendance Summary
        </h3>

        {/* <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border px-3 py-1.5 rounded-md text-sm text-black"
        /> */}



        {/* ✅ ONLY CHANGE HERE (2 INPUTS) */}
        <div className="flex gap-2">
          <input
            type="month"
            value={fromMonth}
            onChange={(e) => setFromMonth(e.target.value)}
            className="border px-3 py-1.5 rounded-md text-sm text-black"
          />

          <input
            type="month"
            value={toMonth}
            onChange={(e) => setToMonth(e.target.value)}
            className="border px-3 py-1.5 rounded-md text-sm text-black"
          />
        </div>




      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-center py-10">
          Loading attendance summary...
        </div>
      )}

      {/* SUMMARY */}
      {!loading && summary && (
        <>
          {/* USER INFO */}
          <div className="flex items-center gap-3 mb-6">
            {summary.profile_pic ? (
              <img
                src={`https://uptask.visiontrek.io${summary.profile_pic}`}
                alt={summary.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                {summary.name?.charAt(0)}
              </div>
            )}

            <div>
              <p className="font-semibold">{summary.name}</p>
            </div>
          </div>

          {/* CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            <Card title="Total Working Days" value={totalWorkingDays} />
            <Card title="Present Days" value={summary.present_days} color="green" />
            <Card title="Absent Days" value={summary.absent_days} color="red" />
            <Card title="Late Days" value={summary.late_days} color="yellow" />
            <Card title="Early Out Days" value={summary.early_out_days} color="purple" />
            <Card title="Extra Hours" value={summary.extra_hours} color="blue" />
            <Card title="Working Hours" value={summary.total_working_hours} color="indigo" />
          </div>
        </>
      )}

      {/* NO DATA */}
      {!loading && !summary && (
        <div className="text-center py-10 text-gray-400">
          No attendance summary found.
        </div>
      )}
    </div>
  );
}

/* CARD */
function Card({ title, value, color }) {
  const colorClasses = {
    green: "bg-green-500/20 text-green-300",
    red: "bg-red-500/20 text-red-300",
    yellow: "bg-yellow-500/20 text-yellow-300",
    purple: "bg-purple-500/20 text-purple-300",
    blue: "bg-blue-500/20 text-blue-300",
    indigo: "bg-indigo-500/20 text-indigo-300",
  };

  return (
    <div
      className={`p-3 md:p-5 rounded-xl backdrop-blur-md ${
        color ? colorClasses[color] : "bg-white/10 text-white"
      }`}
    >
      <p className="text-sm opacity-80">{title}</p>
      <p className="text-xl md:text-2xl font-bold mt-2">
        {value ?? "-"}
      </p>
    </div>
  );
}














// import { useEffect, useState } from "react";
// import { AdminMonthlyAttendanceApi } from "../../app/api";
// import { useAuth } from "../../app/store";

// export default function UserAttendance({ userId }) {
//   const { user } = useAuth();

//   const today = new Date();

//   // ✅ NEW: FROM & TO
//   const [fromMonth, setFromMonth] = useState(
//     `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
//   );

//   const [toMonth, setToMonth] = useState(
//     `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
//   );

//   const [summary, setSummary] = useState(null);
//   const [loading, setLoading] = useState(false);

//   // ✅ UPDATED useEffect
//   useEffect(() => {
//     fetchSummary();
//   }, [fromMonth, toMonth, userId, user]);

//   // ✅ MONTH RANGE HELPER
//   const getMonthRange = (start, end) => {
//     const months = [];
//     let current = new Date(start + "-01");
//     const last = new Date(end + "-01");

//     while (current <= last) {
//       months.push({
//         year: current.getFullYear(),
//         month: String(current.getMonth() + 1).padStart(2, "0"),
//       });
//       current.setMonth(current.getMonth() + 1);
//     }

//     return months;
//   };

//   const fetchSummary = async () => {
//     try {
//       setLoading(true);

//       const months = getMonthRange(fromMonth, toMonth);

//       let finalData = null;

//       for (let m of months) {
//         const res = await AdminMonthlyAttendanceApi(
//           m.month,
//           m.year,
//           userId
//         );

//         if (res?.success && res?.data?.length > 0) {
//           let userData = null;

//           // ✅ SAME ROLE LOGIC (NO CHANGE)
//           if (user?.role_id === 1) {
//             userData = userId
//               ? res.data.find((u) => u.user_id == userId)
//               : res.data[0];
//           } else {
//             userData = res.data.find(
//               (u) => u.user_id == user?.id
//             );
//           }

//           if (!userData) continue;

//           // ✅ MERGE DATA
//           if (!finalData) {
//             finalData = { ...userData };
//           } else {
//             finalData.present_days += userData.present_days || 0;
//             finalData.absent_days += userData.absent_days || 0;
//             finalData.late_days += userData.late_days || 0;
//             finalData.early_out_days += userData.early_out_days || 0;

//             const addTime = (t1, t2) => {
//               const toSec = (t) => {
//                 const [h, m, s] = t.split(":").map(Number);
//                 return h * 3600 + m * 60 + s;
//               };

//               const total = toSec(t1) + toSec(t2);

//               const h = Math.floor(total / 3600);
//               const m = Math.floor((total % 3600) / 60);
//               const s = total % 60;

//               return `${String(h).padStart(2, "0")}:${String(m).padStart(
//                 2,
//                 "0"
//               )}:${String(s).padStart(2, "0")}`;
//             };

//             finalData.total_working_hours = addTime(
//               finalData.total_working_hours,
//               userData.total_working_hours
//             );

//             finalData.extra_hours = addTime(
//               finalData.extra_hours,
//               userData.extra_hours
//             );
//           }
//         }
//       }

//       setSummary(finalData);
//     } catch (err) {
//       console.error("Attendance summary error:", err);
//       setSummary(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const totalWorkingDays =
//     (summary?.present_days || 0) +
//     (summary?.absent_days || 0);

//   return (
//     <div className="text-white">
//       {/* HEADER */}
//       <div className="flex flex-col md:flex-row gap-2 justify-between items-center mb-6">
//         <h3 className="text-xl font-semibold whitespace-nowrap">
//           Monthly Attendance Summary
//         </h3>

        // {/* ✅ ONLY CHANGE HERE (2 INPUTS) */}
        // <div className="flex gap-2">
        //   <input
        //     type="month"
        //     value={fromMonth}
        //     onChange={(e) => setFromMonth(e.target.value)}
        //     className="border px-3 py-1.5 rounded-md text-sm text-black"
        //   />

        //   <input
        //     type="month"
        //     value={toMonth}
        //     onChange={(e) => setToMonth(e.target.value)}
        //     className="border px-3 py-1.5 rounded-md text-sm text-black"
        //   />
        // </div>
//       </div>

//       {/* REST SAME (NO CHANGE) */}
